import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Archive, RefreshCw, LayoutGrid, Columns3, Music, Puzzle } from 'lucide-react';
import { Grid, MusicIcon, VisualArtIcon, WritingIcon, VideoIcon, SoftwareIcon } from '../../components/icons';
import { AppLayout } from '../../components/AppLayout';
import { Button, Card, EmptyState } from '../../components/ui';
import { ProjectKanban, ProjectGrid, ProjectForm, ArchivedProjects, ProjectLimitWarning } from '../../components/Projects';
import { projectsApi, ProjectBoardDto, ProjectDto, CreateProjectRequest } from '../../services';
import './Projects.css';

type ViewMode = 'board' | 'archived' | 'create';
type BoardLayout = 'kanban' | 'grid';
type BoardTab = 'all' | 'Music' | 'Art' | 'Writing' | 'Video' | 'Software';
type ProjectFilter = 'opus' | 'pieces';

const BOARD_TABS: { id: BoardTab; labelKey: string; icon: React.ReactNode }[] = [
  { id: 'all', labelKey: 'works.tabs.all', icon: <Grid size={16} /> },
  { id: 'Music', labelKey: 'works.tabs.music', icon: <MusicIcon size={16} /> },
  { id: 'Art', labelKey: 'works.tabs.art', icon: <VisualArtIcon size={16} /> },
  { id: 'Writing', labelKey: 'works.tabs.writing', icon: <WritingIcon size={16} /> },
  { id: 'Video', labelKey: 'works.tabs.video', icon: <VideoIcon size={16} /> },
  { id: 'Software', labelKey: 'works.tabs.software', icon: <SoftwareIcon size={16} /> },
];

export const Projects: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<ViewMode>('board');
  const [boardLayout, setBoardLayout] = useState<BoardLayout>('kanban');
  const [projectFilter, setProjectFilter] = useState<ProjectFilter>('opus');
  const [activeTab, setActiveTab] = useState<BoardTab>('all');
  const [board, setBoard] = useState<ProjectBoardDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBoard = useCallback(async (tab: BoardTab = activeTab, filter: ProjectFilter = projectFilter) => {
    setLoading(true);
    setError(null);
    try {
      const data = await projectsApi.getProjectBoard({
        projectType: tab === 'all' ? undefined : tab,
        // For pieces, don't use universal stages - let them use their own workflow
        // For opus in "all" tab, use universal stages to show mixed project types
        useUniversalStages: tab === 'all' && filter === 'opus',
        showPiecesOnly: filter === 'pieces',
      });
      setBoard(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('works.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [activeTab, projectFilter, t]);

  useEffect(() => {
    fetchBoard(activeTab, projectFilter);
  }, [activeTab, projectFilter, fetchBoard]);

  const handleTabChange = (tab: BoardTab) => {
    setActiveTab(tab);
  };

  const handleFilterChange = (filter: ProjectFilter) => {
    setProjectFilter(filter);
  };

  const handleCreateProject = async (data: CreateProjectRequest) => {
    try {
      await projectsApi.createProject(data);
      setViewMode('board');
      fetchBoard();
    } catch (err) {
      throw err; // Let the form handle the error
    }
  };

  const handleMoveProject = async (projectId: string, newStage: string) => {
    try {
      // In universal view (all projects), use changeStage as stages are generic
      // In type-specific views, use moveToStage with template validation
      if (activeTab === 'all') {
        await projectsApi.changeStage(projectId, newStage);
      } else {
        await projectsApi.moveToStage(projectId, newStage);
      }
      fetchBoard();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('works.errors.moveFailed'));
    }
  };

  const handleProjectClick = (project: { id: string }) => {
    navigate(`/projects/${project.id}`);
  };

  const handleArchiveProject = async (project: { id: string }) => {
    try {
      await projectsApi.archiveProject(project.id);
      fetchBoard();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('works.errors.archiveFailed'));
    }
  };

  const handleAddProject = () => {
    setViewMode('create');
  };

  const handleRestoreProject = (project: ProjectDto) => {
    fetchBoard();
  };

  if (loading && !board) {
    return (
      <AppLayout>
        <div className="projects-page projects-page--loading">
          <div className="projects-page__spinner" />
          <p>{t('common.loading')}</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="projects-page">
      {/* Header */}
      <header className="projects-page__header">
        <div className="projects-page__title-section">
          <h1 className="projects-page__title">{t('works.title')}</h1>
        </div>

        {/* Board Type Tabs */}
        {viewMode === 'board' && (
          <div className="projects-page__tabs">
            {BOARD_TABS.map((tab) => (
              <button
                key={tab.id}
                className={`projects-page__tab ${activeTab === tab.id ? 'projects-page__tab--active' : ''}`}
                onClick={() => handleTabChange(tab.id)}
              >
                {tab.icon}
                <span>{t(tab.labelKey)}</span>
              </button>
            ))}
          </div>
        )}

        <div className="projects-page__actions">
          {/* Opus/Pieces Toggle */}
          {viewMode === 'board' && (
            <div className="projects-page__filter-toggle">
              <button
                className={`projects-page__filter-btn ${projectFilter === 'opus' ? 'projects-page__filter-btn--active' : ''}`}
                onClick={() => handleFilterChange('opus')}
                aria-label="Show Opus"
                title="Show Opus (parent works)"
              >
                <Music size={16} />
                <span>Opus</span>
              </button>
              <button
                className={`projects-page__filter-btn ${projectFilter === 'pieces' ? 'projects-page__filter-btn--active' : ''}`}
                onClick={() => handleFilterChange('pieces')}
                aria-label="Show Pieces"
                title="Show Pieces (sub-works)"
              >
                <Puzzle size={16} />
                <span>Pieces</span>
              </button>
            </div>
          )}

          {/* View Toggle */}
          {viewMode === 'board' && (
            <div className="projects-page__view-toggle">
              <button
                className={`projects-page__view-btn ${boardLayout === 'kanban' ? 'projects-page__view-btn--active' : ''}`}
                onClick={() => setBoardLayout('kanban')}
                aria-label="Kanban view"
                title="Kanban view"
              >
                <Columns3 size={18} />
              </button>
              <button
                className={`projects-page__view-btn ${boardLayout === 'grid' ? 'projects-page__view-btn--active' : ''}`}
                onClick={() => setBoardLayout('grid')}
                aria-label="Grid view"
                title="Grid view"
              >
                <LayoutGrid size={18} />
              </button>
            </div>
          )}

          <Button
            variant={viewMode === 'archived' ? 'secondary' : 'ghost'}
            onClick={() => setViewMode(viewMode === 'archived' ? 'board' : 'archived')}
          >
            <Archive size={16} />
            {viewMode === 'archived' ? t('common.back') : t('common.archived')}
          </Button>
          <Button variant="ghost" onClick={() => fetchBoard()} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'spinning' : ''} />
          </Button>
          {viewMode !== 'create' && board && board.activeProjects < board.projectLimit && (
            <Button variant="primary" onClick={() => setViewMode('create')}>
              <Plus size={16} />
              {t('works.newProject')}
            </Button>
          )}
        </div>
      </header>

      {/* Error Display */}
      {error && (
        <div className="projects-page__error">
          {error}
          <Button variant="ghost" size="sm" onClick={() => setError(null)}>
            {t('common.dismiss')}
          </Button>
        </div>
      )}

      {/* Project Limit Warning */}
      {board && (
        <ProjectLimitWarning
          currentCount={board.activeProjects}
          limit={board.projectLimit}
          onArchiveClick={() => setViewMode('archived')}
        />
      )}

      {/* Main Content */}
      <main className="projects-page__content">
        {viewMode === 'create' && (
          <Card className="projects-page__form-card">
            <h2>{t('works.createProject')}</h2>
            <ProjectForm
              onSubmit={handleCreateProject}
              onCancel={() => setViewMode('board')}
            />
          </Card>
        )}

        {viewMode === 'board' && board && board.activeProjects === 0 && board.archivedProjects === 0 && (
          <EmptyState
            variant="first-project"
            title={projectFilter === 'pieces' ? 'No pieces yet' : t('works.empty.title')}
            message={projectFilter === 'pieces' ? 'Create an Opus first, then add pieces to it.' : t('works.empty.message')}
            action={projectFilter === 'opus' ? {
              label: t('works.createFirstProject'),
              onClick: () => setViewMode('create'),
              variant: 'primary',
            } : undefined}
          />
        )}

        {viewMode === 'board' && board && (board.activeProjects > 0 || board.archivedProjects > 0) && (
          boardLayout === 'kanban' ? (
            <ProjectKanban
              board={board}
              onMoveProject={handleMoveProject}
              onProjectClick={handleProjectClick}
              onArchiveProject={handleArchiveProject}
              onCreateProject={handleAddProject}
            />
          ) : (
            <ProjectGrid
              board={board}
              onProjectClick={handleProjectClick}
            />
          )
        )}

        {viewMode === 'archived' && (
          <ArchivedProjects onRestore={handleRestoreProject} />
        )}
      </main>
      </div>
    </AppLayout>
  );
};

export default Projects;
